/** Tipovi ugovora `/api/products/stock-lookup` — ogledalo servera, držano lokalno da
 *  frontend bundle ne vuče ništa iz `src/services`. */
export type StockStatus = "instock" | "outofstock" | "onbackorder" | "unknown";
export type StockManagement = "self" | "parent" | "unmanaged" | "unknown";
export type Backorders = "no" | "notify" | "yes" | "unknown";

export type Stock = {
  status: StockStatus;
  quantity: number | null;
  management: StockManagement;
  backorders: Backorders;
};

/** Skladišna lokacija SKU-a: gdje artikal stoji. Bez količine — zaliha je u `Stock`. */
export type StockLocation = { binLabel: string; warehouseName: string };

export type StockLookupVariation = {
  id: string;
  sku: string;
  attributes: { name: string; option: string }[];
  imageUrl: string | null;
  stock: Stock;
  locations: StockLocation[];
};

export type StockLookup = {
  storeId: string;
  storeName: string;
  scannedSku: string;
  matchedVariationId: string | null;
  fetchedAt: string;
  source: "mirror";
  product: {
    id: string;
    name: string;
    sku: string;
    type: "simple" | "variable";
    imageUrl: string | null;
    stock: Stock;
    locations: StockLocation[];
  };
  variations: StockLookupVariation[];
  totalVariations: number;
  complete: true;
};

export const STATUS_LABELS: Record<StockStatus, string> = {
  instock: "na stanju",
  outofstock: "nema",
  onbackorder: "po narudžbi",
  unknown: "nepoznato",
};

/** Kako se jedno stanje ispisuje na tabli: `value` je ono što stoji veliko, `tone` nosi
 *  jedini kolorni signal u aplikaciji (crveno = nula), a `note` objašnjava zašto broja nema.
 *  `quantity: null` nikad ne postaje 0 — zato postoje „—" i „?". */
export type StockView = {
  value: string;
  tone: "strong" | "danger" | "muted";
  status: string;
  note: string | null;
};

export function stockView(stock: Stock): StockView {
  const status = STATUS_LABELS[stock.status] ?? STATUS_LABELS.unknown;
  const backorderNote =
    stock.backorders === "yes" ? "moguća narudžba bez zalihe"
    : stock.backorders === "notify" ? "narudžba uz najavu"
    : null;

  if (stock.management === "unmanaged") {
    return { value: "—", tone: "muted", status, note: "zaliha se ne vodi brojčano" };
  }
  if (stock.management === "unknown" || stock.quantity === null) {
    return { value: "?", tone: "muted", status, note: "stanje nije poznato" };
  }

  const shared = stock.management === "parent" ? "zajednička zaliha proizvoda" : null;
  return {
    value: String(stock.quantity),
    tone: stock.quantity <= 0 ? "danger" : "strong",
    status,
    note: [shared, backorderNote].filter(Boolean).join(" · ") || null,
  };
}

/** Atributi u jedan red: „Veličina M · Boja Teget". Bez atributa se pada na SKU, jer prazan
 *  red na tabli ne govori radniku ništa. */
export function variationTitle(v: StockLookupVariation): string {
  const parts = v.attributes.map((a) => `${a.name} ${a.option}`.trim()).filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : v.sku || "Varijanta";
}

/** Ime skladišta se ispisuje tek kad ih u odgovoru ima više od jednog — inače je police
 *  dovoljno imenovati, a naziv skladišta bi samo trošio red na telefonu. */
export function needsWarehouseName(data: StockLookup): boolean {
  const names = new Set<string>();
  for (const loc of [...data.product.locations, ...data.variations.flatMap((v) => v.locations)]) {
    names.add(loc.warehouseName);
  }
  return names.size > 1;
}

export function locationLabel(loc: StockLocation, withWarehouse: boolean): string {
  const bin = loc.binLabel || "?";
  return withWarehouse && loc.warehouseName ? `${bin} · ${loc.warehouseName}` : bin;
}

/** Poruka za svaki `code` iz ugovora. `title` je šta se desilo, `hint` šta radnik sad radi. */
export function errorCopy(code: string | null, status?: number): { title: string; hint: string } {
  switch (code) {
    case "SKU_NOT_FOUND":
      return { title: "Nema artikla s tim SKU-om", hint: "Provjeri da li je naljepnica iz odabrane trgovine." };
    case "AMBIGUOUS_SKU":
      return { title: "SKU nije jedinstven", hint: "Više artikala dijeli ovaj SKU. Otvori proizvod u Proizvodima da vidiš koji." };
    case "UNSUPPORTED_STOCK_SOURCE":
      return { title: "Provjera stanja nije uključena za ovu trgovinu", hint: "Uključi sinhronizaciju trgovine u Postavkama ili odaberi drugu trgovinu." };
    case "UNSUPPORTED_PRODUCT_TYPE":
      return { title: "Ovaj tip proizvoda se ne prikazuje", hint: "Provjera radi za jednostavne i varijabilne proizvode." };
    case "STORE_NOT_FOUND":
      return { title: "Trgovina više ne postoji", hint: "Odaberi trgovinu u zaglavlju." };
    case "INVALID_INPUT":
      return { title: "Neispravan unos", hint: "SKU može imati najviše 128 znakova." };
    case "STOCK_SOURCE_UNAVAILABLE":
      return { title: "Stanje trenutno nije dostupno", hint: "Skeniraj ponovo za koji trenutak." };
    case "STOCK_LOOKUP_TIMEOUT":
      return { title: "Dohvat je istekao", hint: "Skeniraj ponovo." };
  }
  if (status === 401) return { title: "Prijava je istekla", hint: "Prijavi se ponovo." };
  if (status === 403) return { title: "Nemaš pristup ovoj trgovini", hint: "Odaberi trgovinu za koju imaš pravo pregleda." };
  return { title: "Provjera nije uspjela", hint: "Skeniraj ponovo." };
}

/** Svježina kopije stanja. Odgovor nosi vrijeme sinhronizacije, ne trenutak čitanja, pa
 *  radnik mora vidjeti koliko je staro ono što gleda. */
export function freshness(fetchedAt: string, now: Date = new Date()): string {
  const ms = now.getTime() - Date.parse(fetchedAt);
  if (!Number.isFinite(ms)) return "nepoznato vrijeme";
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "upravo osvježeno";
  if (min < 60) return `osvježeno prije ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `osvježeno prije ${h} h`;
  const d = Math.floor(h / 24);
  return `osvježeno prije ${d} d`;
}
